import { AlwaysAnimatedMaterialOptions } from "@/types";
import { MeshPhysicalMaterial, UnsignedByteType, Color, WebGLCubeRenderTarget, RGBAFormat, LinearMipmapLinearFilter, CubeCamera, DoubleSide } from "three";

export const FoamMaterial = async () => {
	// CubeCamera erstellen
	const cubeRenderTarget = new WebGLCubeRenderTarget(256, {
		format: RGBAFormat,
		generateMipmaps: true,
		minFilter: LinearMipmapLinearFilter,
		type: UnsignedByteType,
	});

	const cubeCamera = new CubeCamera(1, 1000, cubeRenderTarget);
	// scene.add(cubeCamera);

	const material = new MeshPhysicalMaterial({
		color: 0xffffff,
		transparent: true, // Damit der Verlauf die Transparenz beeinflussen kann
		opacity: 0.3,
		roughness: 0,
		metalness: 1,
		side: DoubleSide,
		depthWrite: true,
		// envMap: cubeRenderTarget.texture,
	});

	const uniforms = {
		uTime: { value: 0 },
		uHeight: { value: 20 },
		uColor1: { value: new Color(0x607d8b) },
		uColor2: { value: new Color(0xd0dfe6) }, //new Color(0x87ceeb)
		uColor3: { value: new Color(0xffffff) },
	};

	material.onBeforeCompile = (shader) => {
		// Uniforms für Zeit und Höhe des Verlaufs hinzufügen
		shader.uniforms.uTime = uniforms.uTime;
		shader.uniforms.uHeight = uniforms.uHeight;
		shader.uniforms.uColor1 = uniforms.uColor1;
		shader.uniforms.uColor2 = uniforms.uColor2;
		shader.uniforms.uColor3 = uniforms.uColor3;
		// .uTime = { value: 0 };
		// shader.uniforms.uHeight = { value: 10 }; // Gesamthöhe für den Verlauf
		// shader.uniforms.uColor1 = { value: new Color(0x0000ff) }; // Dunkelblau
		// shader.uniforms.uColor2 = { value: new Color(0x87ceeb) }; // Hellblau
		// shader.uniforms.uColor3 = { value: new Color(0xffffff) }; // Weiß

		// Vertex Shader erweitern
		shader.vertexShader = shader.vertexShader.replace(
			`void main() {`,
			`

      uniform float uTime;
      varying vec3 vTransformed;
      // varying vec4 vWorldPosition;
      void main() {
      `
		);
		shader.vertexShader = shader.vertexShader.replace(
			`#include <begin_vertex>`,
			`
      #include <begin_vertex>

      // Bewegung basierend auf Zeit
      transformed.y += sin(position.x * 2.0 + uTime * 2.0) * 0.1;
      transformed.z += cos(position.y * 2.0 + uTime * 2.0) * 0.1;

      vTransformed = transformed;

      // vWorldPosition = modelMatrix * vec4(transformed, 1.0);
    `
		);

		// Fragment Shader erweitern
		shader.fragmentShader = shader.fragmentShader.replace(
			`#include <common>`,
			`
      #include <common>
      varying vec3 vTransformed; // transformed.y empfangen
      uniform float uHeight;
      uniform vec3 uColor1;
      uniform vec3 uColor2;
      uniform vec3 uColor3;
    `
		);

		// MixColor
		shader.fragmentShader = shader.fragmentShader.replace(
			`#include <lights_physical_fragment>`,
			`
        // Normierte transformed.y-Koordinate für den Verlauf
        float normalizedY = (vTransformed.y + uHeight / 2.0) / uHeight;

        // Verlauf von dunkelblau, hellblau und weiß (ohne Transparenz)
        vec3 gradientColor = mix(uColor1, uColor2, smoothstep(0.0, 0.5, normalizedY));
        gradientColor = mix(gradientColor, uColor3, smoothstep(0.8, 1.0, normalizedY));

        // Albedo (diffuseColor) modifizieren
        diffuseColor.rgb = gradientColor;

        #include <lights_physical_fragment>
      `
		);

		//Roughness
		shader.fragmentShader = shader.fragmentShader.replace(
			`#include <lights_physical_fragment>`,
			`
		    // Dynamische Roughness berechnen
		    float finalRoughness = mix(.0, .30, smoothstep(0.0, 1.0, normalizedY));

		    // Roughness anwenden
		    roughnessFactor = finalRoughness;

		    #include <lights_physical_fragment>
		`
		);

		// MEtallness
		shader.fragmentShader = shader.fragmentShader.replace(
			`#include <lights_physical_fragment>`,
			`
        // Normiere y-Koordinate auf [0, 1]
        // float normalizedY = (vTransformedY + uHeight / 2.0) / uHeight;

        // Metalness-Verlauf: minimal -> maximal -> minimal
        // float metalnessGradient = smoothstep(0.0, 0.6, normalizedY) * (1.0 - smoothstep(0.2, 1.0, normalizedY));

        // Dynamische Metalness berechnen
        float finalMetalness = mix(0., 1.0, smoothstep(0.0, 1.0, normalizedY));

        // Metalness anwenden
        reflectedLight.directSpecular *= finalMetalness;
        reflectedLight.indirectSpecular *= finalMetalness;

        #include <lights_physical_fragment>
    `
		);
	};

	let firstUpdate = true;

	material.userData.always_animated = (opt: AlwaysAnimatedMaterialOptions) => {
		if (firstUpdate) {
			firstUpdate = false;
			// console.log("preventFirstUpdate");

			return;
		}

		if (cubeCamera.parent === null) {
			opt.scene.add(cubeCamera);

			// cubeCamera.position.copy(opt.mesh.position);
			cubeCamera.position.y = 2;
			cubeCamera.position.z = 2;
			cubeCamera.position.x = 8;
		}

		// Temporär das reflektierende Objekt aus der Szene entfernen
		opt.mesh.visible = false;
		opt.gl.setRenderTarget(cubeRenderTarget);
		// Szene aus der Perspektive der CubeCamera rendern
		cubeCamera.update(opt.gl, opt.scene);
		opt.gl.setRenderTarget(null);

		// console.log("CubeCamera position:", cubeCamera.position);
		// console.log("CubeRenderTarget texture:", cubeRenderTarget.texture);
		// Das reflektierende Objekt wieder sichtbar machen
		opt.mesh.visible = true;

		// Szene normal rendern
		uniforms.uTime.value += opt.clock.getDelta();

		if (material.envMap === null && cubeRenderTarget.texture && cubeRenderTarget.texture.source.dataReady) {
			material.envMap = cubeRenderTarget.texture;
		}
	};

	return material;
};
